import React, { useState, useEffect } from 'react';
import { useLocation } from '@reach/router';
import { parse } from 'query-string';
import { Form, Field } from 'react-final-form';
import TextInput from '~components/TextInput';
import Checkbox from '~components/Checkbox';
import { email, text } from '~utils/validations';
import { CircularProgress } from '@material-ui/core';
import { Error } from '../Error/Error';
import styles from './styles.module.scss';
import { invite } from '../../services/checkout';
import { useAnalytics } from '~utils/useAnalytics';

const InvitationForm = ({ onComplete }) => {
  const [error, setError] = useState();
  const location = useLocation();
  const searchParams = parse(location.search);

  const {
    analyticsLoaded,
    trackSubscriptionInvitationPageViewed,
    trackSubscriptionInvitationJoinFamilyTapped,
    trackSubscriptionInvitationConfirmationPageViewed,
    trackSubscriptionInvitationFailurePageViewed,
    saveFormData,
  } = useAnalytics();

  const fields = {
    email: 'email',
    inviteCode: 'invite_code',
    hasAcceptedTerms: 'has_accepted_terms',
  };

  const getFieldInitialValue = (field) => {
    return searchParams[field];
  };

  useEffect(() => {
    analyticsLoaded && trackSubscriptionInvitationPageViewed();
  }, [analyticsLoaded]);

  async function handleSubmit(formData) {
    trackSubscriptionInvitationJoinFamilyTapped(formData[fields.email], formData[fields.inviteCode]);
    saveFormData('invite_form', formData);
    try {
      const { data } = await invite(formData);
      trackSubscriptionInvitationConfirmationPageViewed();
      const familyFirstName = data?.data?.family_name;
      onComplete && onComplete(familyFirstName);
    } catch (e) {
      const detail = e.response?.data?.errors[0]?.detail || 'Something went wrong';
      trackSubscriptionInvitationFailurePageViewed(formData[fields.email], formData[fields.inviteCode], detail);
      setError(detail);
    }
  }

  return (
    <Form
      onSubmit={(formData) => handleSubmit(formData)}
      validate={() => true}
      keepDirtyOnReinitialize
      render={({ handleSubmit, submitting, invalid, values }) => {
        return (
          <div className={styles.container}>
            <form className={styles.formContainer} onSubmit={handleSubmit} noValidate>
              <h2 className={styles.title}>You’re invited</h2>
              <p>To join and start working out for free, make sure you have an eligible iPhone. </p>

              <Field
                className={styles.field}
                name={fields.email}
                component={TextInput}
                label="Email Address"
                type="email"
                validate={email}
                required
                initialValue={getFieldInitialValue(fields.email)}
              />

              <Field
                className={styles.field}
                name={fields.inviteCode}
                component={TextInput}
                label="Invite Code"
                validate={text}
                required
                initialValue={getFieldInitialValue(fields.inviteCode)}
              />

              {error && <Error error={error} className={styles.fullRow} />}
              <div className={styles.termsOfService}>
                <Field
                  required
                  component={Checkbox}
                  name={fields[`hasAcceptedTerms`]}
                  type="checkbox"
                  className={styles.row}
                  label={
                    <>
                      I have read and accept{' '}
                      <a href="https://tempo.fit/terms-of-service" target="_blank" rel="noopener noreferrer">
                        Tempo's Terms of Service
                      </a>
                    </>
                  }
                />
              </div>
              <button
                className={styles.button}
                type="submit"
                disabled={submitting || invalid || !values.has_accepted_terms}
              >
                Submit {submitting && <CircularProgress className={styles.spinner} color="inherit" size={24} />}
              </button>
            </form>
          </div>
        );
      }}
    />
  );
};

InvitationForm.displayName = 'InvitationForm';

export default InvitationForm;
